import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import "./datepicker.css";
import moment from "moment";
import styled from "styled-components";
import ReactTooltip from "react-tooltip";
import {
  Container,
  Box,
  Flex,
  Link,
  List,
  Text,
  Dropdown,
  Heading,
  Divider,
  Input,
  Image,
  Button,
  Icon
} from "samba-ui";
import "react-toggle/style.css";
import humanizeDuration from "humanize-duration";
import { loadAcrQueries, addNewAcrQuery } from "../redux/acrQueries";
import AddNewAcrQueryModal from "./AddNewAcrQueryModal";
import { loadAcrOutages, updateSelectedPage } from "../redux/acrOutages";
import PageLoader from "./PageLoader";
import { getAcrOutageCountries, getAcrOutageCallsigns } from "../api";
import serialize from "../utils/serialize";
import DownloadQueryResultsButton from "./DownloadQueryResultsButton";
import useQuery from "./hooks/useQuery";
import TopChannelsModal from "./TopChannelsModal";
const Checkbox = styled.input`
  width: 20px;
  height: 20px;
`;

const StyledReactTooltip = styled(ReactTooltip)`
  width: 200px;
`;

const range = (start, stop, step) =>
  Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);

const dateFormat = "YYYY-MM-DD HH:mm";

const columns = [
  { param: "incident_state", column: "Incident State", width: 75 },
  { param: "start_timestamp", column: "Start Time", width: 140 },
  { param: "end_timestamp", column: "End Time", width: 140 },
  { param: "duration", column: "Duration", width: 120 },
  { param: "callsign", column: "Callsign", width: 200 },
  { param: "channel_id", column: "Channel Id", width: 50 },
  { param: "channel_country", column: "Country", width: 40 },
  { param: "provider_longname", column: "Provider", width: 140 }
];

const incidentStateOptions = [
  { label: "START", value: "START" },
  { label: "CONTINUE", value: "CONTINUE" },
  { label: "END", value: "END" },
  { label: "ALL", value: "ALL" }
];

const defaultCountry = { label: "ALL", value: "ALL" };
const defaultCallsign = { label: "ALL", value: "ALL" };
const defaultIncidentState = { label: "ALL", value: "ALL" };

const defaultTopChannel = { label: "ALL", value: "ALL" };

const ManageAcrOutages = ({
  user,
  acrOutages,
  loadAcrOutages,
  updateSelectedPage,
  loadAcrQueries,
  addNewAcrQuery,
  history
}) => {
  const isSuperOperator = user.role === "superoperator";
  let query = useQuery();
  const _callsign = query.get("callsign");
  const _provider = query.get("provider");
  const _channelId = query.get("channelId");
  const _state = query.get("incident_state");
  const _country = query.get("country");
  const _duration = query.get("duration");
  const _top_channels = query.get("top_channels");

  const [loading, setLoading] = useState(false);
  const [selectedCallsign, setSelectedCallsign] = useState(
    _callsign ? { label: _callsign, value: _callsign } : defaultCallsign
  );
  const [callsignOptions, setCallsignOptions] = useState([defaultCallsign]);

  const [countryOptions, setCountyOptions] = useState([defaultCountry]);
  const [selectedCountry, setSelectedCountry] = useState(
    _country ? { label: _country, value: _country } : defaultCountry
  );

  const [duration, setDuration] = useState(_duration || 0);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [provider, setProvider] = useState(_provider || "");
  const [channelId, setChannelId] = useState(_channelId || "");

  const [isOpen, setIsOpen] = useState(false);
  const [selectedAcr, setSelectedAcr] = useState({ query: {}, emailList: [] });

  const [incidentState, setIncidentState] = useState(
    _state ? { label: _state, value: _state } : defaultIncidentState
  );

  const [selectedTopChannel, setSelectedTopChannel] = useState(
    defaultTopChannel
  );

  const [filterByTopChannels, setFilterByTopChannels] = useState(_top_channels);

  const [topModalChannelsModalIsOpen, setTopChannelsModalIsOpen] = useState(
    false
  );

  useEffect(() => {
    const req = async () => {
      setLoading(true);
      let queryParam = {
        page: acrOutages.currentPage
      };

      if (selectedCountry.value !== "ALL") {
        queryParam["channel_country"] = selectedCountry.value;
      }

      if (selectedCallsign.value !== "ALL") {
        queryParam["callsign"] = selectedCallsign.value;
      }

      if (provider) {
        queryParam["provider_longname"] = provider;
      }

      if (channelId) {
        queryParam["channel_id"] = channelId;
      }

      if (startDate) {
        queryParam["start_timestamp"] = moment(
          moment.utc(startDate.format(dateFormat)).valueOf()
        ).format(dateFormat);
      }

      if (endDate) {
        queryParam["end_timestamp"] = moment(
          moment.utc(endDate.format(dateFormat)).valueOf()
        ).format(dateFormat);
      }

      if (incidentState.value !== "ALL") {
        queryParam["incident_state"] = incidentState.value;
      }

      if (duration) {
        queryParam["duration"] = duration;
      }

      if (selectedTopChannel.value && selectedTopChannel.value !== "ALL") {
        queryParam["ssid"] = selectedTopChannel.value;
      }

      queryParam["top_channels"] = filterByTopChannels;

      const query = serialize(queryParam);
      await loadAcrOutages(query);
      setLoading(false);
      setSelectedAcr({
        name: "",
        emailList: [],
        query: {
          callsign: selectedCallsign.value,
          country: selectedCountry.value,
          provider,
          duration,
          channel_id: channelId,
          incident_state: incidentState.value,
          ssid: selectedTopChannel.value,
          top_channels: filterByTopChannels
        }
      });
    };
    req();
  }, [
    loadAcrOutages,
    acrOutages.currentPage,
    selectedCountry.value,
    startDate,
    endDate,
    provider,
    channelId,
    duration,
    incidentState,
    selectedCallsign.value,
    selectedTopChannel.value,
    filterByTopChannels
  ]);

  useEffect(() => {
    const req = async () => {
      const result = await getAcrOutageCountries();
      let { data } = await result.json();
      data = data
        .sort((a, b) => a.localeCompare(b))
        .map(d => ({ label: d, value: d }));
      data.unshift(defaultCountry);
      setCountyOptions(data);
    };
    req();
  }, []);

  useEffect(() => {
    const req = async () => {
      const result = await getAcrOutageCallsigns();
      let { data } = await result.json();
      data = data.map(d => ({ label: d, value: d }));
      data.unshift(defaultCallsign);
      setCallsignOptions(data);
    };
    req();
  }, []);

  useEffect(() => {
    const req = async () => {
      await loadAcrQueries();
    };
    req();
  }, [loadAcrQueries]);

  const onDateSelect = (type, date) => {
    if (type === "start") {
      setStartDate(date);
    } else {
      setEndDate(date);
    }
  };

  const paginationOptions = range(1, acrOutages.totalPages, 1).map(p => ({
    label: p,
    value: p
  }));

  const onSaveQueryClick = () => {
    setIsOpen(true);
  };

  const onCallsignChange = selected => {
    setSelectedCallsign(selected);
    updateUrl({ _callsign: selected.value });
  };

  const onProviderChange = e => {
    setProvider(e.target.value);
    updateUrl({ _provider: e.target.value });
  };

  const onChannelIdChange = e => {
    setChannelId(e.target.value);
    updateUrl({ _channelId: e.target.value });
  };

  const onCountryChange = selected => {
    setSelectedCountry(selected);
    updateUrl({ _country: selected.value });
    setSelectedTopChannel(defaultTopChannel);
  };

  const onIncidentStateChange = selected => {
    setIncidentState(selected);
    updateUrl({ _incident_state: selected.value });
  };

  const onDurationChange = e => {
    const { value } = e.target;
    setDuration(value);
    updateUrl({ _duration: value });
  };

  const onFilterByTopChannelsChange = e => {
    const { checked } = e.target;
    setFilterByTopChannels(checked);
    updateUrl({ _top_channels: checked });
  };

  const updateUrl = ({
    _callsign,
    _provider,
    _channelId,
    _country,
    _incident_state,
    _duration,
    _top_channels
  }) => {
    let params = {};
    if (_callsign || (selectedCallsign && selectedCallsign.value !== "ALL")) {
      params["callsign"] = _callsign || selectedCallsign.value;
    }
    if (_country || (selectedCountry && selectedCountry.value !== "ALL")) {
      params["country"] = _country || selectedCountry.value;
    }

    if (_incident_state || (incidentState && incidentState.value !== "ALL")) {
      params["incident_state"] = _incident_state || incidentState.value;
    }
    const checks = ["country", "incident_state", "callsign"];
    checks.forEach(check => {
      if (params[check] === "ALL") {
        delete params[check];
      }
    });

    if (_provider || provider) {
      params["provider"] = _provider || provider;
    }
    if (!params["provider"]) {
      delete params["provider"];
    }

    if (_channelId || channelId) {
      params["channelId"] = _channelId || channelId;
    }

    if (!params["channelId"]) {
      delete params["channelId"];
    }

    if (_duration || duration) {
      params["duration"] = _duration || duration;
    }

    if (params["duration"] === "0") {
      delete params["duration"];
    }

    params["top_channels"] = _top_channels;
    if (!_top_channels) {
      delete params["top_channels"];
    }

    if (incidentState)
      if (Object.keys(params).length) {
        history.replace({
          search: `?${Object.keys(params)
            .map(key => key + "=" + params[key])
            .join("&")}`
        });
      }
  };

  const getData = (outage, param, width) => {
    if (param === "duration") {
      return (
        <Box width={width} key={param}>
          <Text fontSize={1} color="secondary" fontWeight={500}>
            {humanizeDuration(outage["duration"] * 1000, {
              largest: 2,
              maxDecimalPoints: 0
            })}
          </Text>
        </Box>
      );
    } else if (param === "start_timestamp" || param === "end_timestamp") {
      if (outage[param]) {
        return (
          <Box width={width} key={param}>
            <Text fontSize={1} color="secondary" fontWeight={500}>
              {moment(moment.utc(outage[param]).valueOf()).format("lll")}
            </Text>
          </Box>
        );
      }
    } else if (param === "incident_state") {
      let data = "START";
      if (outage[param] === "END") {
        data = "START, END";
      } else if (outage[param] === "CONTINUE") {
        data = "START, CONTINUE";
      }

      return (
        <Box width={width} key={param}>
          <Text fontSize={1} color="secondary" fontWeight={500}>
            {data}
          </Text>
        </Box>
      );
    }

    return (
      <Box width={width} key={param}>
        <Text fontSize={1} color="secondary" fontWeight={500}>
          {outage[param]}
        </Text>
      </Box>
    );
  };

  return (
    <Container maxWidth={2048} minWidth={960}>
      <Flex>
        <Box width="calc(100% - 300px)" pr={4}>
          <Flex>
            <Heading>ACR Outages</Heading>
            <Flex alignItems="center" mb={3} style={{ marginLeft: "auto" }}>
              <Text>Page</Text>
              <Box width={75} px={2}>
                <Dropdown
                  options={paginationOptions}
                  selected={{
                    label: acrOutages.currentPage,
                    value: acrOutages.currentPage
                  }}
                  onSelect={updateSelectedPage}
                />
              </Box>
              of {acrOutages.totalPages}
            </Flex>
          </Flex>
          <List.Header borderColor="#d6d6d6 !important">
            <Flex justifyContent="space-between" alignItems="center">
              <Box width={76} />
              {columns.map(({ column, width }) => (
                <Box width={width} key={column}>
                  <Text fontSize={1} color="secondary" fontWeight={500}>
                    {column}
                  </Text>
                </Box>
              ))}
            </Flex>
          </List.Header>
          {loading ? (
            <PageLoader />
          ) : (
            acrOutages.outages.map((outage, index) => {
              return (
                <List.Item borderColor="#d6d6d6 !important" key={index}>
                  <Flex alignItems="center" justifyContent="space-between">
                    <Box width={76}>
                      <Flex
                        alignItems="center"
                        justifyContent="center"
                        height={"42px !important"}
                        width={"42px"}
                        backgroundColor="#f8f8f7"
                        style={{
                          filter: "drop-shadow(0px 0px 1px rgba(5, 5, 17, 0.5))"
                        }}
                        borderRadius={4}
                      >
                        <Image src={outage.logoUrl} />
                      </Flex>
                    </Box>
                    {columns.map(({ param, width }) =>
                      getData(outage, param, width)
                    )}
                  </Flex>
                </List.Item>
              );
            })
          )}
        </Box>
        <Box width={300} ml={2} mt={52}>
          <Box>
            <Text fontSize={1} fontWeight={500}>
              Country
            </Text>
            <Dropdown
              options={countryOptions}
              selected={selectedCountry}
              onSelect={onCountryChange}
            />
          </Box>
          <Divider my={3} />
          <Box>
            <Text fontSize={1} fontWeight={500}>
              Incident State
            </Text>
            <Dropdown
              options={incidentStateOptions}
              selected={incidentState}
              onSelect={onIncidentStateChange}
            />
          </Box>
          <Box mt={3}>
            <Box mb={2}>
              <Text fontSize={1} fontWeight={500}>
                Callsign
              </Text>
            </Box>
            <Dropdown
              options={callsignOptions}
              selected={selectedCallsign}
              onSelect={onCallsignChange}
            />
          </Box>

          <Box mt={3}>
            <Box mb={2}>
              <Text fontSize={1} fontWeight={500}>
                Provider
              </Text>
            </Box>
            <Input
              style={{ width: "100%" }}
              placeholder="Provider..."
              value={provider}
              onChange={onProviderChange}
            />
          </Box>

          {selectedCountry && selectedCountry.value === "ALL" ? (
            <Box mt={3}>
              <Box mb={2}>
                <Text fontSize={1} fontWeight={500}>
                  Channel Id
                </Text>
              </Box>
              <Input
                style={{ width: "100%" }}
                placeholder="Channel Id..."
                value={channelId}
                onChange={onChannelIdChange}
              />
            </Box>
          ) : (
            <Flex
              mt={3}
              mb={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Text fontSize={1} fontWeight={500}>
                Filter by{" "}
                <Link onClick={() => setTopChannelsModalIsOpen(true)}>
                  the top channels
                </Link>{" "}
                of the {selectedCountry.value}
              </Text>
              <Checkbox
                type="checkbox"
                checked={filterByTopChannels}
                onChange={onFilterByTopChannelsChange}
              />
            </Flex>
          )}

          <Divider my={3} />
          <Box mt={3}>
            <Box mb={2}>
              <Text fontSize={1} fontWeight={500}>
                Set range of incident
              </Text>
            </Box>
            <Flex>
              <Box mr={2}>
                <DatePicker
                  selected={startDate}
                  onChange={date => onDateSelect("start", date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={5}
                  timeCaption="time"
                  dateFormat={dateFormat}
                  maxDate={moment(new Date())}
                />
              </Box>
              <Box>
                <DatePicker
                  selected={endDate}
                  onChange={date => onDateSelect("end", date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={5}
                  timeCaption="time"
                  dateFormat={dateFormat}
                  minDate={startDate}
                  maxDate={moment(new Date())}
                />
              </Box>
            </Flex>
          </Box>
          <Box mt={3}>
            <Flex mb={2} alignItems="center">
              <Text>Duration:</Text>
              <Box ml={2} data-tip="Enter duration in minutes. e.g; 120">
                <Icon name="fas fa-info-circle"></Icon>
              </Box>
            </Flex>
            <Box>
              <Input
                type="number"
                style={{ width: "300px", textAlign: "right" }}
                value={duration}
                onChange={onDurationChange}
                min={0}
              />
            </Box>
          </Box>
          <Box mt={2}>
            <Button onClick={onSaveQueryClick} disabled={isSuperOperator}>
              Save the query
            </Button>
            <DownloadQueryResultsButton
              selectedCountry={selectedCountry}
              incidentState={incidentState}
              selectedCallsign={selectedCallsign}
              provider={provider}
              channelId={channelId}
              startDate={startDate}
              endDate={endDate}
              duration={duration}
              totalSize={acrOutages.totalPages}
            />
          </Box>
        </Box>
      </Flex>
      <AddNewAcrQueryModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        selectedAcr={selectedAcr}
        addNewAcrQuery={addNewAcrQuery}
      />
      <TopChannelsModal
        isOpen={topModalChannelsModalIsOpen}
        onClose={() => setTopChannelsModalIsOpen(false)}
        country={(selectedCountry && selectedCountry.value) || ""}
      />
      <StyledReactTooltip place="right" />
    </Container>
  );
};

ManageAcrOutages.propTypes = {
  acrOutages: PropTypes.object,
  loadAcrOutages: PropTypes.func,
  updateSelectedPage: PropTypes.func,
  loadAcrQueries: PropTypes.func,
  addNewAcrQuery: PropTypes.func
};

const mapStateToProps = (state, props) => ({
  acrOutages: state.acrOutages
});

const mapDispatchToProps = dispatch => ({
  loadAcrOutages: query => dispatch(loadAcrOutages(query)),
  updateSelectedPage: newPage => dispatch(updateSelectedPage(newPage)),
  loadAcrQueries: query => dispatch(loadAcrQueries(query)),
  addNewAcrQuery: body => dispatch(addNewAcrQuery(body))
});

export default connect(mapStateToProps, mapDispatchToProps)(ManageAcrOutages);
